import React from 'react'
import { Prompt } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Add from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'

import Title from '../../component/Title'
import Snackbars from '../../component/Snackbars'
import Pagination from '../../component/Pagination'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import {
    LIST_FILTER_SEARCH_FIELD_SX,
    ListFilterForm,
} from '../../component/ListFilterLayout'
import {
    APP_POPUP_IMAGE_FILE_ACCEPT,
    APP_POPUP_IMAGE_LEAVE_MESSAGE,
} from './appPopupFormUtils'

const PANEL_SX = FORM_PANEL_SX

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    px: 1.25,
    py: 1.25,
    width: '100%',
    '& .MuiRadio-root': {
        mt: -0.1,
        mr: 0.75,
        p: 0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

const settingGroupPanelSx = {
    ...FORM_SUB_PANEL_SX,
}

const DEVICE_TYPE_OPTIONS = [
    { value: 'all', title: '전체', description: 'Android와 iOS 사용자 모두에게 노출합니다.' },
    { value: 'and', title: 'Android', description: 'Android 사용자에게만 노출합니다.' },
    { value: 'ios', title: 'iOS', description: 'iOS 사용자에게만 노출합니다.' },
]

const STATUS_OPTIONS = [
    { value: 1, title: '노출', description: '앱 랜덤 팝업 영역에 표시합니다.' },
    { value: 0, title: '미노출', description: '관리자에서만 보관하고 사용자 화면에는 표시하지 않습니다.' },
]

const POPUP_TYPE_OPTIONS = [
    {
        value: 1,
        title: '애니멀런 리스트',
        description: '클릭 시 애니멀런 목록으로 이동합니다.',
        chipLabel: '기본',
    },
    {
        value: 2,
        title: '애니멀런 상세',
        description: '선택한 상품 상세로 바로 이동합니다.',
        chipLabel: '상품 선택',
        chipColor: 'warning',
    },
    {
        value: 4,
        title: '외부 URL',
        description: '입력한 외부 URL로 이동합니다.',
        chipLabel: 'URL',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

const PRODUCT_PICKER_TABLE_COLUMNS = [
    { width: 70 },
    { width: 120 },
    { width: 420 },
    { width: 120 },
]

const PRODUCT_PICKER_TABLE_MIN_WIDTH = PRODUCT_PICKER_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const PRODUCT_PICKER_TITLE_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const PRODUCT_PICKER_ACTION_CELL_SX = {
    whiteSpace: 'nowrap',
}

// 화면 동작이 달라지는 주요 선택지는 카드 안에 설명을 함께 표시한다.
function ChoiceCardLabel({
    title,
    description,
    chipLabel,
    chipColor = 'default',
}) {
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                {chipLabel && <Chip label={chipLabel} color={chipColor} size="small" variant="outlined" />}
            </Box>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({
    title,
    description,
    name,
    value,
    options,
    disabled,
    onChange,
    columns = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
    panel = false,
}) {
    const content = (
        <>
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: panel ? 800 : 700 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </Box>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: columns,
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                    chipLabel={option.chipLabel}
                                    chipColor={option.chipColor}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </>
    )

    return (
        <FormControl fullWidth>
            {panel ? (
                <Box sx={settingGroupPanelSx}>
                    {content}
                </Box>
            ) : content}
        </FormControl>
    )
}

// 팝업 이미지 미리보기와 파일 선택 버튼을 등록/수정 화면에서 같은 형태로 보여준다.
function AppPopupImageUploader({
    imageSrc,
    imageFile,
    disabled,
    onImageChange,
}) {
    const imageFileName = getImageFileName(imageFile)
    const hasImage = Boolean(imageSrc)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    팝업 이미지
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    JPG, PNG, GIF, WEBP 형식의 이미지를 업로드할 수 있습니다.
                </Typography>
            </Box>
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    height: { xs: 220, md: 260 },
                    mb: 1.5,
                }}
            >
                {hasImage ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="팝업 이미지 미리보기"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <Button
                variant="contained"
                color="success"
                component="label"
                startIcon={<Add />}
                sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                disabled={disabled}
            >
                이미지 선택
                <input
                    hidden
                    name="image"
                    type="file"
                    accept={APP_POPUP_IMAGE_FILE_ACCEPT}
                    disabled={disabled}
                    onChange={onImageChange}
                />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

// 팝업 노출 대상과 이동 방식을 한 섹션에서 조정하도록 묶는다.
function AppPopupSettingsSection({
    params,
    disabled,
    onChange,
}) {
    const deviceType = params.deviceType || 'all'
    const status = params.status ?? 1
    const type = params.type ?? 1

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    노출 설정
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    팝업이 보일 기기, 노출 여부, 클릭 후 이동 방식을 지정합니다.
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                }}
            >
                <SettingChoiceGroup
                    title="노출기종"
                    description="팝업 대상 앱 기기를 선택합니다."
                    name="deviceType"
                    value={deviceType}
                    options={DEVICE_TYPE_OPTIONS}
                    disabled={disabled}
                    onChange={onChange}
                    columns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                    panel
                />
                <SettingChoiceGroup
                    title="노출여부"
                    description="사용자 화면 표시 여부를 선택합니다."
                    name="status"
                    value={status}
                    options={STATUS_OPTIONS}
                    disabled={disabled}
                    onChange={onChange}
                    panel
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <SettingChoiceGroup
                        title="구분"
                        name="type"
                        value={type}
                        options={POPUP_TYPE_OPTIONS}
                        disabled={disabled}
                        onChange={onChange}
                        columns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                    />
                </Box>
                {Number(type) === 4 && (
                    <Box sx={{ gridColumn: '1 / -1', maxWidth: 720 }}>
                        <TextField
                            label="URL"
                            id="landingUrl"
                            name="landingUrl"
                            value={params.landingUrl || ''}
                            size="small"
                            fullWidth
                            disabled={disabled}
                            onChange={onChange}
                        />
                    </Box>
                )}
            </Box>
        </Paper>
    )
}

// 애니멀런 상세 팝업에서 연결할 상품을 검색하고 선택하는 공통 폼 영역이다.
function AppPopupProductPicker({
    visible,
    saving,
    productDatas,
    selectedMeetId,
    totalCount,
    curPage,
    offset,
    searchProductTitle,
    onSearchProductTitleChange,
    onSearch,
    onChooseProduct,
    setCurPage,
}) {
    // 저장 폼 안에서도 Enter 입력이 상품 조회만 실행되도록 키 이벤트를 분리한다.
    const handleSearchKeyDown = (event) => {
        if (event.key !== 'Enter') {
            return
        }

        event.preventDefault()
        onSearch()
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                ...FORM_PANEL_SX,
                display: visible ? 'flex' : 'none',
                flexDirection: 'column',
                gap: 1.5,
                opacity: saving ? 0.5 : 1,
                pointerEvents: saving ? 'none' : 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        연결할 상품 선택
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        상품명을 검색한 뒤 노출할 애니멀런 상품을 선택하세요.
                    </Typography>
                </Box>
                <ListFilterForm component="div" role="search" sx={{ pt: 0, pb: 0 }}>
                    <InputBase
                        sx={{ ...LIST_FILTER_SEARCH_FIELD_SX, flex: '0 0 250px' }}
                        placeholder="상품명 검색"
                        type="productTitle"
                        name="productTitle"
                        value={searchProductTitle}
                        disabled={saving}
                        onChange={onSearchProductTitleChange}
                        onKeyDown={handleSearchKeyDown}
                    />
                    <IconButton type="button" aria-label="상품 검색" onClick={onSearch} disabled={saving}>
                        <SearchIcon />
                    </IconButton>
                </ListFilterForm>
            </Box>
            <EmbeddedTableContainer>
                <Table size="small" sx={{ minWidth: PRODUCT_PICKER_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                    <colgroup>
                        {PRODUCT_PICKER_TABLE_COLUMNS.map((column, index) => (
                            <col key={`app-popup-product-column-${index}`} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                    <caption>Total Count: {totalCount}</caption>
                    <TableHead>
                        <TableRow>
                            <TableCell>No</TableCell>
                            <TableCell>상품이미지</TableCell>
                            <TableCell>상품명</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {productDatas.map((row) => {
                            const selected = row.meetId === selectedMeetId

                            return (
                                <TableRow
                                    key={row.meetId}
                                    sx={{ backgroundColor: selected ? 'action.selected' : 'inherit' }}
                                >
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>
                                        <Box
                                            component="img"
                                            src={`${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`}
                                            alt=""
                                            sx={{
                                                width: 64,
                                                height: 40,
                                                objectFit: 'cover',
                                                borderRadius: '3px',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                display: 'block',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={PRODUCT_PICKER_TITLE_CELL_SX}>{row.title || '-'}</TableCell>
                                    <TableCell align="right" sx={PRODUCT_PICKER_ACTION_CELL_SX}>
                                        <Button
                                            type="button"
                                            variant={selected ? 'outlined' : 'contained'}
                                            color="success"
                                            size="small"
                                            onClick={() => onChooseProduct(row.meetId)}
                                            disabled={saving}
                                        >
                                            {selected ? '선택됨' : '선택'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {productDatas.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                    검색 결과가 없습니다.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </EmbeddedTableContainer>
            {totalCount > 0 && (
                <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />
            )}
        </Paper>
    )
}

// App 랜덤 팝업 등록/수정 화면에서 공통으로 쓰는 폼 레이아웃만 담당한다.
export default function AppPopupFormPage({
    title,
    params,
    image,
    saving,
    leaveGuardEnabled,
    listState,
    snackbar,
    productPicker,
    onSubmit,
    onImageChange,
    onFieldChange,
    onBackClick,
}) {
    const {
        search: listSearch,
        curPage: listCurPage,
        searchSelect: listSearchSelect,
    } = listState
    const {
        open: openSnackbar,
        severity: severitySnackbar,
        message: messageSnackbar,
        onClose: handleClose,
    } = snackbar
    const {
        visible: productPickerVisible,
        totalCount,
        curPage,
        setCurPage,
        offset,
        productDatas,
        searchProductTitle,
        onSearchProductTitleChange,
        onSearch,
        onChooseProduct,
    } = productPicker

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={APP_POPUP_IMAGE_LEAVE_MESSAGE} />
                <Title>{title}</Title>

                <Box component="form" encType="multipart/form-data" onSubmit={onSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <AppPopupImageUploader
                                imageSrc={image}
                                imageFile={params.image}
                                disabled={saving}
                                onImageChange={onImageChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <AppPopupSettingsSection
                                params={params}
                                disabled={saving}
                                onChange={onFieldChange}
                            />
                        </Grid>
                        {productPickerVisible && (
                            <Grid item xs={12} sm={12} md={12}>
                                <AppPopupProductPicker
                                    visible
                                    saving={saving}
                                    productDatas={productDatas}
                                    selectedMeetId={params.meetId}
                                    totalCount={totalCount}
                                    curPage={curPage}
                                    offset={offset}
                                    searchProductTitle={searchProductTitle}
                                    onSearchProductTitleChange={onSearchProductTitleChange}
                                    onSearch={onSearch}
                                    onChooseProduct={onChooseProduct}
                                    setCurPage={setCurPage}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <BottomButtonGroup
                        variant="formAction"
                        path="/appPopups"
                        search={listSearch}
                        visible={true}
                        curPage={listCurPage}
                        searchSelect={listSearchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={onBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
